import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {BaseService, UUID} from '@nh/angular-tses';
import {PermissionsService} from 'app/permissions/permissions.service';
import {GlobalAction} from 'app/permissions/types';
import {DasqlNodeFactory} from 'app/search/query/dasql-node-factory';
import {EqualityRelation} from 'app/search/query/enums/equality-relation';
import {SearchResult} from 'app/search/query/enums/search-result';
import {DasqlNode} from 'app/search/query/nodes/dasql-node';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {SearchColumn} from '../../search/query/enums/search-column';
import {Discipline, DisciplineService} from './discipline.service';
import {Organisation, OrganisationService} from './organisation.service';

export enum DashboardQuery {
  WAITING_ON_ME = 'WAITING_ON_ME',
  IM_INVOLVED_IN = 'IM_INVOLVED_IN',
  MY_ORGANISATIONS = 'MY_ORGANISATIONS',
  WITHDRAWN_BEFORE_SUBMISSION = 'WITHDRAWN_BEFORE_SUBMISSION',
  MY_DISCIPLINES = 'MY_DISCIPLINES',
  POST_DETERMINATION = 'POST_DETERMINATION',
}

export type DashboardEntryMap = Partial<Record<DashboardQuery, SearchResult>>;

/**
 * Creates a search node for departures that only includes those that are open, with the post determination actions
 * not completed. This is defined as any of the following not being true: the Realised Cost Benefit having a value,
 * all locations being incorporated with date or not incorporated (calculated in backend), all conditions having
 * their PD actions complete (calculated in backend).
 *
 * Note this is exported as this logic is shared between the post determination actions dashboard filter and the bulk
 * reassignment modal search.
 */
export function postDeterminationNotCompleteNode(): DasqlNode {
  return DasqlNodeFactory.condition(
    SearchColumn.ALL_POST_DETERMINATION_ACTIONS_COMPLETE,
    EqualityRelation.NOT_EQUAL,
    'Yes'
  );
}

@Injectable({
  providedIn: 'root',
})
export class DashboardService extends BaseService {
  private readonly querySubject: BehaviorSubject<DashboardQuery> = new BehaviorSubject(DashboardQuery.WAITING_ON_ME);

  private readonly filterShowMap: Record<DashboardQuery, boolean> = {
    [DashboardQuery.WAITING_ON_ME]: true,
    [DashboardQuery.IM_INVOLVED_IN]: true,
    [DashboardQuery.MY_ORGANISATIONS]: true,
    [DashboardQuery.MY_DISCIPLINES]: true,
    [DashboardQuery.WITHDRAWN_BEFORE_SUBMISSION]: true,
    [DashboardQuery.POST_DETERMINATION]: true,
  };

  constructor(
    protected _snackbar: MatSnackBar,
    private _permissionsService: PermissionsService,
    private organistionService: OrganisationService,
    private disciplineService: DisciplineService
  ) {
    super(_snackbar);
  }

  public async createFilterNode(filter: DashboardQuery, userId: UUID): Promise<DasqlNode> {
    await this._permissionsService.whenReady();

    const atomicNodes: DasqlNode[] = [];
    const isAdmin: boolean = this._permissionsService.isAdmin();

    switch (filter) {
      case DashboardQuery.WAITING_ON_ME:
        isAdmin
          ? atomicNodes.push(
              DasqlNodeFactory.or(
                DasqlNodeFactory.condition(SearchColumn.ASSIGNEE_UUID, EqualityRelation.EQUAL, userId.value),
                DasqlNodeFactory.condition(SearchColumn.WORK_FLOW_STATE, EqualityRelation.EQUAL, 'DAS_ADMIN')
              )
            )
          : atomicNodes.push(
              DasqlNodeFactory.condition(SearchColumn.ASSIGNEE_UUID, EqualityRelation.EQUAL, userId.value)
            );
        atomicNodes.push(this.notWithdrawnBeforeRecievedNode());
        atomicNodes.push(DasqlNodeFactory.condition(SearchColumn.DATE_DETERMINED, EqualityRelation.EQUAL, null));
        break;
      case DashboardQuery.IM_INVOLVED_IN:
        if (!isAdmin) {
          atomicNodes.push(DasqlNodeFactory.role(userId));
        }
        atomicNodes.push(this.notWithdrawnBeforeRecievedNode());
        atomicNodes.push(DasqlNodeFactory.condition(SearchColumn.DATE_DETERMINED, EqualityRelation.EQUAL, null));
        break;
      case DashboardQuery.MY_ORGANISATIONS:
        const orgsNodes: DasqlNode[] = [];
        const orgs: Organisation[] = await this.organistionService.getAUsersOrganisations(userId).toPromise();

        if (!orgs || !orgs.length) {
          return Promise.resolve(null);
        }

        orgs.forEach((organisation: Organisation) => {
          orgsNodes.push(
            DasqlNodeFactory.condition(SearchColumn.DESIGN_ORGANISATION_ID, EqualityRelation.EQUAL, organisation.id)
          );
        });
        atomicNodes.push(DasqlNodeFactory.or(...orgsNodes));
        atomicNodes.push(this.notWithdrawnBeforeRecievedNode());
        atomicNodes.push(DasqlNodeFactory.condition(SearchColumn.DATE_DETERMINED, EqualityRelation.EQUAL, null));
        break;
      case DashboardQuery.MY_DISCIPLINES:
        const discNodes: DasqlNode[] = [];
        const discs: Discipline[] = await this.disciplineService.getAUsersDisciplines(userId).toPromise();

        if (!discs || !discs.length) {
          return Promise.resolve(null);
        }

        discs.forEach((discipline: Discipline) => {
          discNodes.push(
            DasqlNodeFactory.condition(SearchColumn.SUBJECT, EqualityRelation.EQUAL, discipline.displayName)
          );
        });
        atomicNodes.push(DasqlNodeFactory.or(...discNodes));
        atomicNodes.push(this.notWithdrawnBeforeRecievedNode());
        atomicNodes.push(DasqlNodeFactory.condition(SearchColumn.DATE_DETERMINED, EqualityRelation.EQUAL, null));
        break;
      case DashboardQuery.WITHDRAWN_BEFORE_SUBMISSION:
        if (!isAdmin) {
          atomicNodes.push(DasqlNodeFactory.role(userId));
        }
        atomicNodes.push(DasqlNodeFactory.condition(SearchColumn.WORK_FLOW_STATE, EqualityRelation.EQUAL, 'WITHDRAWN'));
        atomicNodes.push(DasqlNodeFactory.condition(SearchColumn.DATE_RECEIVED_AT_HE, EqualityRelation.EQUAL, null));
        break;
      case DashboardQuery.POST_DETERMINATION:
        atomicNodes.push(this.isDeterminedApproved());
        if (!isAdmin) {
          atomicNodes.push(
            DasqlNodeFactory.condition(SearchColumn.PROJECT_MANAGER_UUID, EqualityRelation.EQUAL, userId.value)
          );
        }
        atomicNodes.push(postDeterminationNotCompleteNode());
        break;
    }

    atomicNodes.push(
      DasqlNodeFactory.condition(SearchColumn.DEPARTURE_ID_NUMBER, EqualityRelation.GREATER_THAN_OR_EQUAL_TO, 100000)
    );
    return DasqlNodeFactory.and(...atomicNodes);
  }

  private isDeterminedApproved(): DasqlNode {
    return DasqlNodeFactory.or(
      ...[
        DasqlNodeFactory.condition(SearchColumn.WORK_FLOW_STATE, EqualityRelation.EQUAL, 'APPROVED'),
        DasqlNodeFactory.condition(SearchColumn.WORK_FLOW_STATE, EqualityRelation.EQUAL, 'APPROVED_WITH_CONDITIONS'),
      ]
    );
  }

  public onQueryChange(): Observable<DashboardQuery> {
    return this.querySubject.asObservable();
  }

  public setQueryState(query: DashboardQuery): void {
    this.querySubject.next(query);
  }

  public getFiltersToShow(): Observable<Record<DashboardQuery, boolean>> {
    return combineLatest([
      this._permissionsService.can(GlobalAction.VIEW_MY_ORGANISATIONS_FILTER),
      this._permissionsService.can(GlobalAction.VIEW_MY_DISCIPLINES_FILTER),
      this._permissionsService.can(GlobalAction.VIEW_POST_DETERMINATION_FILTER),
    ]).pipe(
      map(([canViewOrgs, canViewDiscs, canViewPostDetermination]: [boolean, boolean, boolean]) => {
        this.filterShowMap.MY_ORGANISATIONS = canViewOrgs;
        this.filterShowMap.MY_DISCIPLINES = canViewDiscs;
        this.filterShowMap.POST_DETERMINATION = canViewPostDetermination;
        return this.filterShowMap;
      })
    );
  }

  private notWithdrawnBeforeRecievedNode(): DasqlNode {
    return DasqlNodeFactory.not(
      DasqlNodeFactory.and(
        DasqlNodeFactory.condition(SearchColumn.WORK_FLOW_STATE, EqualityRelation.EQUAL, 'WITHDRAWN'),
        DasqlNodeFactory.condition(SearchColumn.DATE_RECEIVED_AT_HE, EqualityRelation.EQUAL, null)
      )
    );
  }
}
